import { useQuery } from "@apollo/client";
import { extractKeyedSearchTerms } from "encamp-omnisearch/src/utils/keyedSearchTerms";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo } from "react";
import { removeKeyInOmnisearch } from "utils/omnisearchUtils";

import { LATEST_INVOICED_PERIOD_QUERY } from "./invoiceTrackingQueries";

/** The omnisearch key the Invoice Tracking tabs share for their period. */
export const PERIOD_KEY = "invoiceDate";

/** Inclusive on both ends, as `YYYY-MM-DD`. Empty until a default is known. */
export type InvoiceTrackingPeriod = { from: string; through: string };

/**
 * Days into a month before its newest service date counts as that month being
 * billed in full; earlier, the prior month is the last complete one.
 */
const TRAILING_DAYS = 7;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function billedMonth(latestServiceDate: string | null | undefined): string {
  if (latestServiceDate == null || !ISO_DATE.test(latestServiceDate)) return "";
  const [year, month, day] = latestServiceDate.split("-").map(Number);
  if (year == null || month == null || day == null) return "";
  if (day > TRAILING_DAYS) return latestServiceDate.slice(0, 7);
  const previous = new Date(Date.UTC(year, month - 2, 1));
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * A calendar month is the period vendors bill in. An empty month has no bounds
 * to state, and the tabs ask for nothing until one is known.
 */
export function monthBounds(month: string): InvoiceTrackingPeriod {
  if (!/^\d{4}-\d{2}$/.test(month)) return { from: "", through: "" };
  const [year, index] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year ?? 0, index ?? 0, 0)).getUTCDate();
  return { from: `${month}-01`, through: `${month}-${String(lastDay).padStart(2, "0")}` };
}

/** The period a search states, or empty bounds when it states none this bar can show. */
export function periodFromSearch(search: string): InvoiceTrackingPeriod {
  const [terms] = extractKeyedSearchTerms(search);
  const dates = terms.filter(({ key }) => key === PERIOD_KEY);
  const value = dates.length === 1 && dates[0]?.value.length === 1 ? dates[0].value[0] : undefined;
  const empty = { from: "", through: "" };
  if (!value) return empty;
  const [from, through] = value.includes("..") ? value.split("..") : [value, value];
  if (!from || !through || !ISO_DATE.test(from) || !ISO_DATE.test(through)) return empty;
  return { from, through };
}

/**
 * The search without the shared period term, for a tab that grades the period
 * instead of filtering by it and refuses unknown keys.
 */
export function withoutPeriodTerm(search: string): string {
  return removeKeyInOmnisearch(search, PERIOD_KEY);
}

/**
 * The period as a token the grids hide from their filter pills; the bar above
 * the tabs is its one control.
 */
export function periodDisplayTokens(
  period: InvoiceTrackingPeriod
): readonly { key: string; value: string }[] {
  if (!period.from || !period.through) return [];
  return [{ key: PERIOD_KEY, value: `${period.from}..${period.through}` }];
}

/**
 * Reads only the search: a pill exists only for a term that is there, so the
 * default period is irrelevant.
 */
export function usePeriodDisplayTokens(): readonly { key: string; value: string }[] {
  const { omnisearch } = useOmnisearchStore();
  return useMemo(() => periodDisplayTokens(periodFromSearch(omnisearch)), [omnisearch]);
}

/** The period alone, as a query string for a link that must carry it. */
export function periodSearch(period: InvoiceTrackingPeriod): string {
  if (!period.from || !period.through) return "";
  const omnisearch = `${PERIOD_KEY}:${period.from}..${period.through}`;
  return `?${new URLSearchParams({ omnisearch })}`;
}

/** Clears every filter but the period, which the bar above the tabs still owns. */
export function useClearFiltersToPeriod(): () => void {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  return useCallback(() => {
    const { from, through } = periodFromSearch(omnisearch);
    setOmnisearchImmediate(from && through ? `${PERIOD_KEY}:${from}..${through}` : "");
  }, [omnisearch, setOmnisearchImmediate]);
}

/** Why the chosen range cannot be used, or null. Shown against the picker. */
export function periodError(period: InvoiceTrackingPeriod): string | null {
  const { from, through } = period;
  if (from === "" || through === "") return null;
  return from > through ? "The period ends before it starts." : null;
}

/**
 * The period every Invoice Tracking tab answers for, held in omnisearch (and so
 * the URL). Set once above the tabs; each tab applies it in its own terms, so
 * switching tabs keeps the same dataset.
 */
export function useInvoiceTrackingPeriod(): {
  period: InvoiceTrackingPeriod;
  setPeriod: (next: { from?: string; through?: string }) => void;
  /** True once a period is settled, so a tab knows not to ask for all of time. */
  ready: boolean;
} {
  const { tenantId } = useTenant();
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues } = useOmnisearchActions();
  const { data } = useQuery(LATEST_INVOICED_PERIOD_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });

  const stated = useMemo(() => periodFromSearch(omnisearch), [omnisearch]);
  const fallback = monthBounds(billedMonth(data?.wasteInvoices.items[0]?.servicePeriodEnd));
  const period = stated.from && stated.through ? stated : fallback;

  const setPeriod = useCallback(
    (next: { from?: string; through?: string }) => {
      const { from, through } = next;
      editKeyValues([[PERIOD_KEY, from && through ? `${from}..${through}` : ""]]);
    },
    [editKeyValues]
  );

  return { period, setPeriod, ready: Boolean(period.from && period.through) };
}
